import { useApiTestingLanguage } from './useApiTestingLanguage';
import React from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Link2, 
  Globe, 
  ArrowRight, 
  RefreshCw, 
  PlayCircle, 
  Edit, 
  Trash2 
} from 'lucide-react';

/**
 * ChainCard - Displays a single API test chain with visual data flow
 * 
 * This is a display component that receives callbacks for actions.
 * All state management remains in the parent (APITesting.js)
 * 
 * @param {Object} chain - The chain object
 * @param {Function} onExecute - Callback when Run Chain is clicked
 * @param {Function} onEdit - Callback when Edit is clicked
 * @param {Function} onDelete - Callback when Delete is clicked
 * @param {boolean} isExecuting - Whether this chain is currently executing
 * @param {boolean} isAnyExecuting - Whether any chain is currently executing
 */
export const ChainCard = ({ 
  chain, 
  onExecute, 
  onEdit, 
  onDelete, 
  isExecuting, 
  isAnyExecuting 
}) => {
  const { t, language } = useApiTestingLanguage();
  return (
    <Card className="p-4" data-testid={`chain-card-${chain.id}`}>
      <div className="flex justify-between items-start">
        <div className="flex-1">
          {/* Header */}
          <div className="flex items-center gap-2 mb-2">
            <Link2 className="h-5 w-5 text-purple-600" />
            <h4 className="font-semibold text-gray-900">{chain.name}</h4>
            <Badge variant="outline" className="text-xs">
              {chain.steps?.length || 0} {t("steps")}
            </Badge>
          </div>
          
          {chain.description && (
            <p className="text-sm text-gray-500 mb-3">{chain.description}</p>
          )}
          
          {/* Visual Data Flow Graph */}
          <ChainFlowGraph steps={chain.steps || []} />

          {/* Footer info */}
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>{t("Created")} {new Date(chain.created_at).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US')}</span>
            {chain.environment_id && (
              <span className="flex items-center gap-1">
                <Globe className="h-3 w-3" />
                {t("Environment linked")}
              </span>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 ml-4">
          <Button
            size="sm"
            onClick={() => onExecute(chain.id)}
            disabled={isAnyExecuting}
            className="bg-green-600 hover:bg-green-700"
            data-testid={`btn-execute-chain-${chain.id}`}
          >
            {isExecuting ? (
              <>
                <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                {t("Running...")}
              </>
            ) : isAnyExecuting ? (
              <>
                <PlayCircle className="h-4 w-4 mr-1 opacity-50" />
                {t("Wait...")}
              </>
            ) : (
              <>
                <PlayCircle className="h-4 w-4 mr-1" />
                {t("Run Chain")}
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            aria-label={t("Edit")}
            onClick={() => onEdit(chain)}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            aria-label={t("Delete")}
            onClick={() => onDelete(chain.id)}
            className="text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

/**
 * ChainFlowGraph - Visual representation of chain steps and variable flow
 * Internal component, not exported separately
 */
const ChainFlowGraph = ({ steps }) => {
  const { t } = useApiTestingLanguage();
  if (!steps || steps.length === 0) return null;

  return (
    <div className="bg-gradient-to-r from-gray-50 to-white border rounded-lg p-3 mb-3">
      <div className="flex items-start gap-3 overflow-x-auto pb-2">
        {steps.map((step, idx) => {
          const extractions = step.test_details?.extractions || [];
          const previousExtractions = steps
            .slice(0, idx)
            .flatMap(s => s.test_details?.extractions || []);
          
          return (
            <React.Fragment key={idx}>
              {/* Step Node */}
              <StepNode 
                step={step} 
                index={idx}
                extractions={extractions}
                previousExtractions={previousExtractions}
              />
              
              {/* Arrow connector with variable flow indicator */}
              {idx < steps.length - 1 && (
                <StepConnector extractionsCount={extractions.length} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="flex items-center gap-4 mt-2 pt-2 border-t text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-green-100 border border-green-300"></span>
          GET
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-blue-100 border border-blue-300"></span>
          POST
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-green-100"></span>
          <span className="font-mono">var</span> {t("= Extracted variable")}
        </span>
      </div>
    </div>
  );
};

/**
 * StepNode - Individual step in the flow graph
 */
const StepNode = ({ step, index, extractions, previousExtractions }) => {
  const { t } = useApiTestingLanguage();
  const getStepBorderColor = (method) => {
    switch (method) {
      case 'GET': return 'border-green-300 bg-green-50';
      case 'POST': return 'border-blue-300 bg-blue-50';
      case 'PUT': return 'border-yellow-300 bg-yellow-50';
      case 'DELETE': return 'border-red-300 bg-red-50';
      default: return 'border-gray-300 bg-gray-50';
    }
  };

  return (
    <div className="flex flex-col items-center min-w-[140px]">
      {/* Incoming variables indicator */}
      {index > 0 && previousExtractions.length > 0 && (
        <div className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded mb-1 whitespace-nowrap">
          {t("Uses:")} {previousExtractions.slice(0, 2).map(e => `{{${e.variable_name}}}`).join(', ')}
          {previousExtractions.length > 2 && ` +${previousExtractions.length - 2}`}
        </div>
      )}
      
      {/* Step card */}
      <div className={`border-2 rounded-lg p-2 text-center w-full ${getStepBorderColor(step.test_details?.method)}`}>
        <div className="flex items-center justify-center gap-1 mb-1">
          <span className="w-5 h-5 rounded-full bg-purple-600 text-white text-xs flex items-center justify-center font-bold">
            {index + 1}
          </span>
          <Badge variant="outline" className="text-xs">
            {step.test_details?.method || 'GET'}
          </Badge>
        </div>
        <div className="font-medium text-sm truncate" title={step.test_details?.name}>
          {step.test_details?.name || step.test_id.slice(0, 8)}
        </div>
        {step.stop_on_fail && (
          <div className="text-xs text-red-500 mt-1">{t("Stop on fail")}</div>
        )}
      </div>
      
      {/* Outgoing variables */}
      {extractions.length > 0 && (
        <div className="mt-2 text-center">
          <div className="text-xs text-gray-500 mb-1">{t("Extracts:")}</div>
          <div className="flex flex-wrap gap-1 justify-center">
            {extractions.map((ext, extIdx) => (
              <span 
                key={extIdx}
                className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-mono"
                title={`${ext.source}: ${ext.path || 'N/A'}`}
              >
                {ext.variable_name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * StepConnector - Arrow between steps showing variable flow
 */
const StepConnector = ({ extractionsCount }) => {
  return (
    <div className="flex flex-col items-center justify-center self-center min-w-[60px]">
      {extractionsCount > 0 ? (
        <>
          <div className="text-xs text-green-600 whitespace-nowrap mb-1">
            {extractionsCount} var{extractionsCount > 1 ? 's' : ''}
          </div>
          <div className="flex items-center">
            <div className="w-8 h-0.5 bg-green-400"></div>
            <ArrowRight className="h-5 w-5 text-green-500 -ml-1" />
          </div>
        </>
      ) : (
        <div className="flex items-center">
          <div className="w-8 h-0.5 bg-gray-300"></div>
          <ArrowRight className="h-5 w-5 text-gray-400 -ml-1" />
        </div>
      )}
    </div>
  );
};

export default ChainCard;
